import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { LanguageToggle } from "@/components/ui/LanguageToggle";
import type { ReactNode } from "react";

interface AuthLayoutProps {
  children: ReactNode;
}

/**
 * Shared layout for all auth pages (login, register, forgot-password, etc.).
 *
 * Centered card with:
 * - Top-right toolbar (theme toggle + language switch)
 * - Aether brand mark (gradient logo)
 * - Glassmorphism card
 * - Bottom migration banner
 */
export function AuthLayout({ children }: AuthLayoutProps) {
  const { t } = useTranslation();

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center bg-void px-4 py-12">
      {/* Toolbar */}
      <div className="absolute top-4 right-4 flex items-center gap-1">
        <ThemeToggle />
        <LanguageToggle />
      </div>

      {/* Brand */}
      <Link to="/login" className="mb-8 flex items-center gap-3 group">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo to-cyan shadow-lg shadow-indigo/20 transition-transform group-hover:scale-105">
          <span className="text-lg font-bold text-white">A</span>
        </div>
        <span className="text-xl font-bold tracking-tight text-heading">{t("common.appName")}</span>
      </Link>

      {/* Card */}
      <div className="w-full max-w-md rounded-2xl border border-border bg-surface/80 p-8 shadow-xl shadow-black/5 backdrop-blur-sm">
        {children}
      </div>

      {/* Migration banner */}
      <p className="mt-8 max-w-md text-center text-xs text-muted">{t("auth.codepushBanner")}</p>
    </div>
  );
}
